// src/components/payroll/ApprovePaySlip.jsx
// Admin / Principal / Dean use this to review and approve salary slip requests.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { salarySlipAPI } from '../../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtC = (v) =>
  v != null
    ? `₹${parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '₹0.00';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_META = {
  PENDING:  { label: 'Pending',  color: '#b5882a', bg: '#fdf6e7', icon: '⏳' },
  APPROVED: { label: 'Approved', color: '#3d9e6e', bg: '#ecf7f2', icon: '✅' },
  REJECTED: { label: 'Rejected', color: '#c95f5f', bg: '#fdf0f0', icon: '❌' },
};

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
  .aps-wrap { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; }
  .aps-hdr  { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; flex-wrap: wrap; gap: 10px; }
  .aps-filters { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
  .aps-select {
    padding: 7px 10px; border: 1px solid #e2e6ed; border-radius: 8px;
    font-size: 13px; background: #fff; color: #1e2636; outline: none; cursor: pointer; font-family: inherit;
  }
  .aps-input {
    padding: 7px 12px; border: 1px solid #e2e6ed; border-radius: 8px;
    font-size: 13px; color: #1e2636; outline: none; font-family: inherit; background: #fff;
  }
  /* stats bar */
  .aps-stats { display: flex; gap: 10px; margin-bottom: 18px; flex-wrap: wrap; }
  .aps-stat  {
    flex: 1; min-width: 100px; background: #fff; border: 1px solid #e2e6ed;
    border-radius: 10px; padding: 12px 16px; text-align: center;
    box-shadow: 0 1px 4px rgba(0,0,0,.05);
  }
  .aps-stat-val   { font-size: 24px; font-weight: 800; line-height: 1.1; }
  .aps-stat-label { font-size: 11px; color: #9aa3b4; font-weight: 600; margin-top: 3px; }
  /* table */
  .aps-tbl-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .aps-tbl { width: 100%; border-collapse: collapse; min-width: 700px; }
  .aps-tbl th {
    padding: 9px 14px; text-align: left; font-size: 10px; font-weight: 700;
    letter-spacing: .08em; text-transform: uppercase; color: #9aa3b4;
    border-bottom: 1px solid #e2e6ed; background: #f8f9fb; white-space: nowrap;
  }
  .aps-tbl td {
    padding: 11px 14px; font-size: 13px; color: #5a6478;
    border-bottom: 1px solid #eaedf2; vertical-align: middle;
  }
  .aps-tbl tr:hover td { background: #f8f9fb; }
  .aps-badge {
    display: inline-block; padding: 3px 10px; border-radius: 20px;
    font-size: 11px; font-weight: 700;
  }
  .aps-btn {
    padding: 6px 13px; border-radius: 7px; font-size: 12px; font-weight: 600;
    cursor: pointer; border: none; font-family: inherit;
    transition: opacity .15s, transform .1s;
  }
  .aps-btn:hover   { opacity: .88; }
  .aps-btn:active  { transform: scale(.97); }
  .aps-btn:disabled { opacity: .45; cursor: not-allowed; }
  .aps-btn-primary { background: #4a7fc1; color: #fff; }
  .aps-btn-approve { background: #3d9e6e; color: #fff; }
  .aps-btn-reject  { background: #fdf0f0; color: #c95f5f; border: 1px solid #e8baba; }
  .aps-btn-ghost   { background: #eef0f4; color: #5a6478; }
  .aps-btn-view    { background: #eef3fb; color: #4a7fc1; }
  /* modal */
  .aps-overlay {
    position: fixed; inset: 0; background: rgba(30,38,54,.45);
    display: flex; align-items: center; justify-content: center;
    z-index: 200; backdrop-filter: blur(4px); padding: 16px;
  }
  .aps-modal {
    background: #fff; border-radius: 16px; border: 1px solid #e2e6ed;
    width: 100%; max-width: 840px; max-height: 92vh;
    display: flex; flex-direction: column; overflow: hidden;
    box-shadow: 0 8px 32px rgba(0,0,0,.14);
  }
  .aps-modal-hdr {
    padding: 16px 22px; border-bottom: 1px solid #e2e6ed;
    display: flex; align-items: center; justify-content: space-between;
    background: #f8f9fb; gap: 12px;
  }
  .aps-modal-body { padding: 22px; overflow-y: auto; flex: 1; }
  /* slip preview */
  .slip-page {
    background: #fff; border: 1px solid #c5ccdb; border-radius: 10px;
    padding: 28px 32px; max-width: 720px; margin: 0 auto;
    font-size: 13px; color: #1e2636; line-height: 1.6;
  }
  .slip-logo-row  { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
  .slip-title     { font-size: 20px; font-weight: 800; color: #4a7fc1; }
  .slip-subtitle  { font-size: 11px; color: #9aa3b4; }
  .slip-divider   { border: none; border-top: 1px solid #e2e6ed; margin: 14px 0; }
  .slip-section   { margin-bottom: 16px; }
  .slip-sec-title {
    font-size: 10px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase;
    color: #4a7fc1; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 2px solid #eef3fb;
  }
  .slip-fields    { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }
  .slip-field     { background: #f8f9fb; border-radius: 6px; padding: 7px 11px; }
  .slip-f-label   { font-size: 10px; color: #9aa3b4; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; }
  .slip-f-val     { font-size: 13px; color: #1e2636; font-weight: 600; margin-top: 1px; }
  .slip-table     { width: 100%; border-collapse: collapse; }
  .slip-table th  { text-align: left; padding: 6px 11px; font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #9aa3b4; background: #f8f9fb; }
  .slip-table td  { padding: 7px 11px; border-bottom: 1px solid #eaedf2; font-size: 13px; }
  .slip-table tr:last-child td { border-bottom: none; }
  .slip-net       { background: #eef3fb; border-radius: 8px; padding: 12px 14px; display: flex; justify-content: space-between; align-items: center; margin-top: 10px; }
  .slip-net-label { font-size: 14px; font-weight: 700; }
  .slip-net-val   { font-size: 22px; font-weight: 800; color: #3d9e6e; }
  /* action panel */
  .aps-action-panel {
    background: #f8f9fb; border: 1px solid #e2e6ed; border-radius: 10px;
    padding: 18px; margin-top: 20px;
  }
  .aps-action-title { font-size: 12px; font-weight: 700; color: #1e2636; margin-bottom: 12px; }
  .aps-textarea {
    width: 100%; min-height: 80px; padding: 10px 12px;
    border: 1px solid #e2e6ed; border-radius: 8px; resize: vertical;
    font-family: inherit; font-size: 13px; color: #1e2636; outline: none;
    background: #fff; box-sizing: border-box;
  }
  .aps-action-btns { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ─── Slip Preview (read-only, identical layout to employee view) ───────────────
function SlipPreview({ request }) {
  const pr  = request.payroll_detail || {};
  const ld  = pr.leave_details || [];

  const deductionRows = [];
  if (parseFloat(pr.leave_deduction || 0) > 0) {
    deductionRows.push({
      label:  `Leave Deduction (${pr.unpaid_leave_days} unpaid day${pr.unpaid_leave_days !== 1 ? 's' : ''})`,
      amount: pr.leave_deduction,
    });
  }
  if (parseFloat(pr.late_deduction || 0) > 0) {
    deductionRows.push({
      label:  `Late Arrival Penalty (${pr.late_days} late arrivals → ${pr.late_penalty_days} day${pr.late_penalty_days !== 1 ? 's' : ''} deducted)`,
      amount: pr.late_deduction,
    });
  }
  if (parseFloat(pr.other_deductions || 0) > 0) {
    deductionRows.push({ label: 'Other Deductions', amount: pr.other_deductions });
  }

  return (
    <div className="slip-page">
      <div className="slip-logo-row">
        <div>
          <div className="slip-title">🏥 Medical College ERP</div>
          <div className="slip-subtitle">Salary Slip — Confidential</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 700, color: '#1e2636', fontSize: 14 }}>{pr.month_name} {pr.year}</div>
          <div style={{ fontSize: 11, color: '#9aa3b4' }}>Preview</div>
        </div>
      </div>
      <hr className="slip-divider" />

      <div className="slip-section">
        <div className="slip-sec-title">Employee Information</div>
        <div className="slip-fields">
          <div className="slip-field"><div className="slip-f-label">Full Name</div><div className="slip-f-val">{request.user_name || '—'}</div></div>
          <div className="slip-field"><div className="slip-f-label">Employee ID</div><div className="slip-f-val" style={{ fontFamily: 'monospace' }}>{request.user_employee_id || '—'}</div></div>
          <div className="slip-field"><div className="slip-f-label">Role / Designation</div><div className="slip-f-val">{pr.user_subrole || pr.user_role || '—'}</div></div>
          <div className="slip-field"><div className="slip-f-label">Department</div><div className="slip-f-val">{request.user_department || pr.user_department || '—'}</div></div>
          <div className="slip-field"><div className="slip-f-label">Salary Period</div><div className="slip-f-val">{pr.month_name} {pr.year}</div></div>
          <div className="slip-field"><div className="slip-f-label">Daily Rate</div><div className="slip-f-val">{fmtC(pr.daily_salary)}</div></div>
        </div>
      </div>

      <div className="slip-section">
        <div className="slip-sec-title">Earnings</div>
        <table className="slip-table">
          <thead><tr><th>Description</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
          <tbody>
            <tr><td>Basic Monthly Salary</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtC(pr.base_salary)}</td></tr>
            <tr style={{ background: '#f8f9fb' }}><td style={{ fontWeight: 700 }}>Gross Salary</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtC(pr.gross_salary)}</td></tr>
          </tbody>
        </table>
      </div>

      {deductionRows.length > 0 && (
        <div className="slip-section">
          <div className="slip-sec-title">Deductions</div>
          <table className="slip-table">
            <thead><tr><th>Description</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
            <tbody>
              {deductionRows.map((d, i) => (
                <tr key={i}>
                  <td>{d.label}</td>
                  <td style={{ textAlign: 'right', color: '#c95f5f', fontWeight: 600 }}>- {fmtC(d.amount)}</td>
                </tr>
              ))}
              <tr style={{ background: '#fdf0f0' }}>
                <td style={{ fontWeight: 700 }}>Total Deductions</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#c95f5f' }}>- {fmtC(pr.total_deductions)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Leave detail breakdown */}
      {ld.length > 0 && (
        <div className="slip-section">
          <div className="slip-sec-title">Leave Breakdown</div>
          <table className="slip-table">
            <thead>
              <tr>
                <th>Type</th><th>From</th><th>To</th>
                <th>Days</th><th>Paid/LOP</th>
                <th style={{ textAlign: 'right' }}>Deduction</th>
              </tr>
            </thead>
            <tbody>
              {ld.map((l, i) => (
                <tr key={i}>
                  <td>{l.leave_type_display}</td>
                  <td>{l.from_date}</td>
                  <td>{l.to_date}</td>
                  <td>{l.days_in_month}</td>
                  <td>
                    <span style={{ fontSize: 11, fontWeight: 700, color: l.is_paid ? '#3d9e6e' : '#c95f5f' }}>
                      {l.is_paid ? 'Paid' : 'LOP'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', color: '#c95f5f' }}>
                    {parseFloat(l.deduction_amount || 0) > 0 ? `- ${fmtC(l.deduction_amount)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Late arrival detail */}
      {parseFloat(pr.late_days || 0) > 0 && (
        <div className="slip-section">
          <div className="slip-sec-title">Late Arrival Details</div>
          <div className="slip-fields">
            <div className="slip-field"><div className="slip-f-label">Late Occurrences</div><div className="slip-f-val">{pr.late_days}</div></div>
            <div className="slip-field"><div className="slip-f-label">Penalty Days (÷3 rule)</div><div className="slip-f-val" style={{ color: '#c95f5f' }}>{pr.late_penalty_days}</div></div>
            <div className="slip-field"><div className="slip-f-label">Late Penalty Amount</div><div className="slip-f-val" style={{ color: '#c95f5f' }}>- {fmtC(pr.late_deduction)}</div></div>
            <div className="slip-field"><div className="slip-f-label">Rule</div><div className="slip-f-val" style={{ fontSize: 11 }}>Every 3 lates = 1 day salary deduction</div></div>
          </div>
        </div>
      )}

      <div className="slip-net">
        <span className="slip-net-label">💰 Net Salary Payable</span>
        <span className="slip-net-val">{fmtC(pr.net_salary)}</span>
      </div>

      {request.status === 'APPROVED' && (
        <div style={{ background: '#ecf7f2', border: '1px solid #b2dcc8', borderRadius: 8, padding: '10px 14px', marginTop: 14, fontSize: 12, color: '#3d9e6e' }}>
          ✅ Approved by {request.approved_by_name || '—'} on {fmtDate(request.approved_at)}
          {request.notes && <div style={{ marginTop: 4, color: '#5a6478' }}>Note: {request.notes}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Review Modal ─────────────────────────────────────────────────────────────
function ReviewModal({ request, onClose, onDone }) {
  const [action,   setAction]   = useState('');    // 'approve' | 'reject'
  const [reason,   setReason]   = useState('');
  const [notes,    setNotes]    = useState('');
  const [busy,     setBusy]     = useState(false);
  const [error,    setError]    = useState('');
  const printRef = useRef(null);

  const isPending = request.status === 'PENDING';

  const handleSubmit = async () => {
    if (!action) return;
    if (action === 'reject' && !reason.trim()) {
      setError('Please provide a rejection reason.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await salarySlipAPI.approveOrReject(request.id, {
        action,
        rejection_reason: reason || undefined,
        notes:            notes  || undefined,
      });
      onDone();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.detail || 'Action failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank', 'width=900,height=700');
    win.document.write(`
      <html><head><title>Salary Slip — ${request.user_name}</title>
      <style>
        body { font-family: 'Segoe UI', system-ui, sans-serif; margin: 0; padding: 20px; color: #1e2636; }
        ${CSS}
      </style></head>
      <body>${content.innerHTML}</body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  const sm = STATUS_META[request.status] || STATUS_META.PENDING;

  return (
    <div className="aps-overlay" onClick={onClose}>
      <div className="aps-modal" onClick={e => e.stopPropagation()}>
        <div className="aps-modal-hdr">
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1e2636' }}>
              Review Slip — {request.user_name}
            </div>
            <div style={{ fontSize: 12, color: '#9aa3b4', marginTop: 3 }}>
              {MONTH_NAMES[request.payroll_detail?.month] || ''} {request.payroll_detail?.year}
              {' · '}
              <span style={{ color: sm.color, fontWeight: 700 }}>{sm.icon} {sm.label}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {request.status === 'APPROVED' && (
              <button className="aps-btn aps-btn-primary" onClick={handlePrint}>🖨️ Print</button>
            )}
            <button className="aps-btn aps-btn-ghost" onClick={onClose}>✕ Close</button>
          </div>
        </div>

        <div className="aps-modal-body" ref={printRef}>
          {/* Slip Preview */}
          <SlipPreview request={request} />

          {/* Action Panel — only for pending requests */}
          {isPending && (
            <div className="aps-action-panel">
              <div className="aps-action-title">⚖️ Approval Decision</div>
              {error && (
                <div style={{ background: '#fdf0f0', border: '1px solid #e8baba', borderRadius: 7, padding: '8px 12px', color: '#c95f5f', fontSize: 13, marginBottom: 12 }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <button
                  className="aps-btn aps-btn-approve"
                  style={{ flex: 1, opacity: action === 'approve' ? 1 : .6, outline: action === 'approve' ? '2px solid #3d9e6e' : 'none' }}
                  onClick={() => setAction('approve')}
                >
                  ✅ Approve
                </button>
                <button
                  className="aps-btn aps-btn-reject"
                  style={{ flex: 1, opacity: action === 'reject' ? 1 : .6, outline: action === 'reject' ? '2px solid #c95f5f' : 'none' }}
                  onClick={() => setAction('reject')}
                >
                  ❌ Reject
                </button>
              </div>

              {action === 'reject' && (
                <>
                  <label style={{ fontSize: 12, color: '#5a6478', fontWeight: 600, display: 'block', marginBottom: 5 }}>
                    Rejection Reason *
                  </label>
                  <textarea
                    className="aps-textarea"
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Explain why this slip is being rejected…"
                  />
                </>
              )}

              {action === 'approve' && (
                <>
                  <label style={{ fontSize: 12, color: '#5a6478', fontWeight: 600, display: 'block', marginBottom: 5 }}>
                    Optional Note
                  </label>
                  <textarea
                    className="aps-textarea"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Optional note to attach to this approval…"
                    style={{ minHeight: 60 }}
                  />
                </>
              )}

              <div className="aps-action-btns">
                <button
                  className={`aps-btn ${action === 'reject' ? 'aps-btn-reject' : 'aps-btn-approve'}`}
                  disabled={!action || busy}
                  onClick={handleSubmit}
                >
                  {busy ? '⏳ Processing…' : action === 'approve' ? '✅ Confirm Approval' : action === 'reject' ? '❌ Confirm Rejection' : 'Select an action above'}
                </button>
                <button className="aps-btn aps-btn-ghost" onClick={onClose}>Cancel</button>
              </div>
            </div>
          )}

          {request.status === 'REJECTED' && request.rejection_reason && (
            <div style={{ background: '#fdf0f0', border: '1px solid #e8baba', borderRadius: 8, padding: '12px 14px', marginTop: 16, fontSize: 13, color: '#c95f5f' }}>
              <strong>Rejection reason:</strong> {request.rejection_reason}
              <br />
              <span style={{ fontSize: 11, color: '#9aa3b4' }}>
                Rejected by {request.approved_by_name || '—'} on {fmtDate(request.approved_at)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function ApprovePaySlip() {
  const [requests, setRequests]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [selected, setSelected]   = useState(null);
  const [filterStatus, setFilterStatus] = useState('PENDING');
  const [search, setSearch]       = useState('');
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await salarySlipAPI.getAllRequests(
        filterStatus ? { status: filterStatus } : {}
      );
      setRequests(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      setError('Failed to load salary slip requests.');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleDone = () => {
    setSuccess('Action completed successfully.');
    setTimeout(() => setSuccess(''), 4000);
    load();
  };

  // Counts
  const counts = {
    PENDING:  requests.length > 0 ? 0 : 0,
    APPROVED: 0,
    REJECTED: 0,
  };
  // Re-build counts from all (we always show counts regardless of filter)
  const [allRequests, setAllRequests] = useState([]);
  useEffect(() => {
    salarySlipAPI.getAllRequests({})
      .then(d => setAllRequests(Array.isArray(d) ? d : d.results || []))
      .catch(() => {});
  }, [requests]);

  const totalCounts = {
    PENDING:  allRequests.filter(r => r.status === 'PENDING').length,
    APPROVED: allRequests.filter(r => r.status === 'APPROVED').length,
    REJECTED: allRequests.filter(r => r.status === 'REJECTED').length,
    total:    allRequests.length,
  };

  // Filter by search
  const filtered = requests.filter(r => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (r.user_name || '').toLowerCase().includes(q) ||
      (r.user_employee_id || '').toLowerCase().includes(q) ||
      (r.user_department || '').toLowerCase().includes(q) ||
      String(r.payroll_detail?.month_name || '').toLowerCase().includes(q) ||
      String(r.payroll_detail?.year || '').includes(q)
    );
  });

  const sm = (status) => STATUS_META[status] || STATUS_META.PENDING;

  return (
    <div className="aps-wrap">
      <style>{CSS}</style>

      {/* Alerts */}
      {error && (
        <div style={{ background: '#fdf0f0', border: '1px solid #e8baba', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#c95f5f', display: 'flex', justifyContent: 'space-between' }}>
          {error}
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c95f5f', fontWeight: 700 }}>✕</button>
        </div>
      )}
      {success && (
        <div style={{ background: '#ecf7f2', border: '1px solid #b2dcc8', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#3d9e6e' }}>
          ✅ {success}
        </div>
      )}

      {/* Stats bar */}
      <div className="aps-stats">
        <div className="aps-stat" style={{ borderTop: '3px solid #e2e6ed' }}>
          <div className="aps-stat-val" style={{ color: '#1e2636' }}>{totalCounts.total}</div>
          <div className="aps-stat-label">Total Requests</div>
        </div>
        <div className="aps-stat" style={{ borderTop: `3px solid ${STATUS_META.PENDING.color}`, cursor: 'pointer' }} onClick={() => setFilterStatus('PENDING')}>
          <div className="aps-stat-val" style={{ color: STATUS_META.PENDING.color }}>{totalCounts.PENDING}</div>
          <div className="aps-stat-label">Pending</div>
        </div>
        <div className="aps-stat" style={{ borderTop: `3px solid ${STATUS_META.APPROVED.color}`, cursor: 'pointer' }} onClick={() => setFilterStatus('APPROVED')}>
          <div className="aps-stat-val" style={{ color: STATUS_META.APPROVED.color }}>{totalCounts.APPROVED}</div>
          <div className="aps-stat-label">Approved</div>
        </div>
        <div className="aps-stat" style={{ borderTop: `3px solid ${STATUS_META.REJECTED.color}`, cursor: 'pointer' }} onClick={() => setFilterStatus('REJECTED')}>
          <div className="aps-stat-val" style={{ color: STATUS_META.REJECTED.color }}>{totalCounts.REJECTED}</div>
          <div className="aps-stat-label">Rejected</div>
        </div>
      </div>

      {/* Filters */}
      <div className="aps-hdr">
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1e2636' }}>
          📄 Salary Slip Requests
          {filterStatus && (
            <span className="aps-badge" style={{ marginLeft: 10, color: sm(filterStatus).color, background: sm(filterStatus).bg }}>
              {sm(filterStatus).label}
            </span>
          )}
        </div>
        <div className="aps-filters">
          <input
            className="aps-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Search by name, ID, dept…"
            style={{ width: 220 }}
          />
          <select className="aps-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e2e6ed', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 52 }}>
            <div style={{ width: 28, height: 28, border: '2px solid #e2e6ed', borderTop: '2px solid #4a7fc1', borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '52px 20px', color: '#9aa3b4' }}>
            <div style={{ fontSize: 32, marginBottom: 8, opacity: .4 }}>📭</div>
            <p style={{ fontSize: 13 }}>
              {filterStatus === 'PENDING'
                ? 'No pending slip requests — all caught up!'
                : 'No requests found matching your filters.'}
            </p>
          </div>
        ) : (
          <div className="aps-tbl-wrap">
            <table className="aps-tbl">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Period</th>
                  <th>Net Salary</th>
                  <th>Late Penalty</th>
                  <th>Requested On</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const pr    = r.payroll_detail || {};
                  const s     = sm(r.status);
                  const hasLatePenalty = parseFloat(pr.late_days || 0) > 0;
                  return (
                    <tr key={r.id}>
                      <td>
                        <div style={{ fontWeight: 600, color: '#1e2636' }}>{r.user_name || '—'}</div>
                        <div style={{ fontSize: 11, color: '#9aa3b4', fontFamily: 'monospace' }}>
                          {r.user_employee_id || r.user_role}
                        </div>
                      </td>
                      <td style={{ fontSize: 12 }}>{r.user_department || '—'}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{pr.month_name} {pr.year}</div>
                        <div style={{ fontSize: 11, color: '#9aa3b4' }}>
                          {pr.status_display || pr.status}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 700, color: '#3d9e6e' }}>{fmtC(pr.net_salary)}</div>
                        <div style={{ fontSize: 11, color: '#c95f5f' }}>
                          Deductions: {fmtC(pr.total_deductions)}
                        </div>
                      </td>
                      <td>
                        {hasLatePenalty ? (
                          <div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#b5882a' }}>
                              {pr.late_days} late → {pr.late_penalty_days} day{pr.late_penalty_days !== 1 ? 's' : ''}
                            </span>
                            <div style={{ fontSize: 11, color: '#c95f5f' }}>- {fmtC(pr.late_deduction)}</div>
                          </div>
                        ) : (
                          <span style={{ color: '#9aa3b4', fontSize: 12 }}>None</span>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>{fmtDate(r.requested_at)}</td>
                      <td>
                        <span className="aps-badge" style={{ color: s.color, background: s.bg, border: `1px solid ${s.color}44` }}>
                          {s.icon} {s.label}
                        </span>
                        {r.status !== 'PENDING' && r.approved_by_name && (
                          <div style={{ fontSize: 10, color: '#9aa3b4', marginTop: 3 }}>
                            by {r.approved_by_name}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button
                            className="aps-btn aps-btn-view"
                            onClick={() => setSelected(r)}
                          >
                            {r.status === 'PENDING' ? '⚖️ Review' : '👁 View'}
                          </button>
                          {r.status === 'PENDING' && (
                            <>
                              <button
                                className="aps-btn aps-btn-approve"
                                onClick={async () => {
                                  try {
                                    await salarySlipAPI.approveOrReject(r.id, { action: 'approve' });
                                    handleDone();
                                  } catch (e) {
                                    setError('Quick approve failed. Please use the review modal.');
                                  }
                                }}
                              >
                                ✅
                              </button>
                              <button
                                className="aps-btn aps-btn-reject"
                                title="Reject (requires reason)"
                                onClick={() => setSelected(r)}
                              >
                                ❌
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {filtered.length > 0 && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid #eaedf2', fontSize: 12, color: '#9aa3b4', textAlign: 'right' }}>
            Showing {filtered.length} of {requests.length} request{requests.length !== 1 ? 's' : ''}
            {search && ` matching "${search}"`}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {selected && (
        <ReviewModal
          request={selected}
          onClose={() => setSelected(null)}
          onDone={handleDone}
        />
      )}
    </div>
  );
}